import React from "react";
import { FiEdit2, FiInfo, FiPlus, FiRefreshCw, FiTrash2 } from "react-icons/fi";

import type { Memory } from "types/Memory";
import useMemoriesQuery from "services/dbApi/hooks/useMemoriesQuery";
import {
  useDeleteMemoryMutation,
  useDeleteUserMemoriesMutation,
  usePatchMemoryMutation,
  usePostMemoryMutation,
} from "services/dbApi/hooks/useMemoryMutations";
import {
  MAX_MEMORY_CHARS,
  THREAD_MEMORY_CAP,
  USER_MEMORY_CAP,
  memoryErrorMessage,
  memoryLength,
  useMemoryEnabled,
} from "utilities/memory";
import MemoryInfoModal from "./MemoryInfoModal";
import styles from "./MemoryView.module.scss";

interface MemoryEditorProps {
  initialText: string;
  label: string;
  /** Resolves to an error message to show, or undefined on success. */
  onSave: (text: string) => Promise<string | undefined>;
  onCancel: () => void;
}

/** A textarea with a live count, used to add and to edit a memory. */
function MemoryEditor({
  initialText,
  label,
  onSave,
  onCancel,
}: MemoryEditorProps) {
  const [text, setText] = React.useState(initialText);
  const [error, setError] = React.useState<string>();
  const [saving, setSaving] = React.useState(false);
  const length = memoryLength(text);
  const canSave = length > 0 && length <= MAX_MEMORY_CHARS && !saving;

  const save = async () => {
    setSaving(true);
    const message = await onSave(text.trim());
    setSaving(false);
    setError(message);
  };

  return (
    <div className={styles.editor}>
      <textarea
        className={styles.textarea}
        aria-label={label}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        autoFocus
      />
      <div className={styles.editorFooter}>
        <span
          className={
            length > MAX_MEMORY_CHARS ? styles.countOver : styles.count
          }
        >
          {length}/{MAX_MEMORY_CHARS}
        </span>
        <button type="button" className={styles.textButton} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={save}
          disabled={!canSave}
        >
          Save
        </button>
      </div>
      {error && (
        <p className={styles.errorText} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function MemoryRow({ memory }: { memory: Memory }) {
  const [editing, setEditing] = React.useState(false);
  const patchMutation = usePatchMemoryMutation();
  const deleteMutation = useDeleteMemoryMutation();

  if (editing) {
    return (
      <li className={styles.row}>
        <MemoryEditor
          initialText={memory.content}
          label="Edit memory"
          onCancel={() => setEditing(false)}
          onSave={async (content) => {
            try {
              await patchMutation.mutateAsync({ id: memory.id, content });
              setEditing(false);
              return undefined;
            } catch (error) {
              return memoryErrorMessage(error);
            }
          }}
        />
      </li>
    );
  }

  const remove = () => {
    if (window.confirm("Delete this memory?")) {
      deleteMutation.mutate(memory.id);
    }
  };

  return (
    <li className={styles.row}>
      <p className={styles.content}>{memory.content}</p>
      {deleteMutation.isError && (
        <p className={styles.errorText} role="alert">
          Couldn&apos;t delete this memory
        </p>
      )}
      <div className={styles.rowFooter}>
        {memory.source === "agent" && (
          <span className={styles.tag}>Saved by assistant</span>
        )}
        <button
          type="button"
          className={styles.iconButton}
          onClick={() => setEditing(true)}
          aria-label="Edit memory"
        >
          <FiEdit2 aria-hidden="true" />
        </button>
        <button
          type="button"
          className={styles.iconButton}
          onClick={remove}
          aria-label="Delete memory"
          disabled={deleteMutation.isPending}
        >
          <FiTrash2 aria-hidden="true" />
        </button>
      </div>
    </li>
  );
}

interface MemorySectionProps {
  title: string;
  memories: Memory[];
  cap: number;
  /** Set for the chat's section; new memories are pinned to it. */
  threadId?: string;
  emptyText: string;
  onDeleteAll?: () => void;
}

function MemorySection({
  title,
  memories,
  cap,
  threadId,
  emptyText,
  onDeleteAll,
}: MemorySectionProps) {
  const [adding, setAdding] = React.useState(false);
  const postMutation = usePostMemoryMutation();
  const atCap = memories.length >= cap;

  return (
    <section className={styles.section} aria-label={title}>
      <div className={styles.sectionHeader}>
        <h4 className={styles.sectionTitle}>{title}</h4>
        {onDeleteAll && memories.length > 0 && (
          <button
            type="button"
            className={styles.textButton}
            onClick={onDeleteAll}
          >
            Delete all
          </button>
        )}
      </div>
      {memories.length === 0 && !adding && (
        <p className={styles.emptyText}>{emptyText}</p>
      )}
      <ul className={styles.list}>
        {memories.map((memory) => (
          <MemoryRow key={memory.id} memory={memory} />
        ))}
      </ul>
      {adding ? (
        <MemoryEditor
          initialText=""
          label={`New memory for ${title}`}
          onCancel={() => setAdding(false)}
          onSave={async (content) => {
            try {
              await postMutation.mutateAsync({ content, threadId });
              setAdding(false);
              return undefined;
            } catch (error) {
              return memoryErrorMessage(error);
            }
          }}
        />
      ) : (
        <>
          <button
            type="button"
            className={styles.addButton}
            onClick={() => setAdding(true)}
            disabled={atCap}
            aria-label={`Add memory to ${title}`}
          >
            <FiPlus aria-hidden="true" />
            <span>Add</span>
          </button>
          {atCap && (
            <p className={styles.hint}>
              Limit reached. Delete or edit a memory to add another.
            </p>
          )}
        </>
      )}
    </section>
  );
}

interface MemoryViewProps {
  /** The open chat, once its thread has loaded. Undefined on the new-chat route. */
  threadId?: string;
}

/**
 * The memory view of the right-hand pane (UI ADR 078): what the assistant
 * remembers about the user and what is pinned to this chat.
 */
export default function MemoryView({ threadId }: MemoryViewProps) {
  const [enabled, setEnabled] = useMemoryEnabled();
  const query = useMemoriesQuery(threadId);
  const deleteAllMutation = useDeleteUserMemoriesMutation();
  const [isInfoOpen, setIsInfoOpen] = React.useState(false);

  const memories = query.data ?? [];
  const userMemories = memories.filter((m) => m.threadId === null);
  const threadMemories = memories.filter(
    (m) => threadId !== undefined && m.threadId === threadId
  );
  // errorUpdatedAt stays set while a retry is in flight, so the error UI does
  // not flash back to the loading text (ADR 066).
  const failed = query.errorUpdatedAt > 0 && query.data === undefined;

  const deleteAll = () => {
    if (
      window.confirm(
        "Delete everything the assistant remembers about you? Memories pinned to chats stay."
      )
    ) {
      deleteAllMutation.mutate();
    }
  };

  let content: React.ReactNode;
  if (failed) {
    content = (
      <div className={styles.loadError}>
        <p className={styles.errorMessage}>Couldn&apos;t load memories</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className={styles.retryButton}
          disabled={query.isFetching}
        >
          <FiRefreshCw
            aria-hidden="true"
            className={query.isFetching ? styles.retryIconSpinning : undefined}
          />
          <span>{query.isFetching ? "Loading…" : "Retry"}</span>
        </button>
      </div>
    );
  } else if (query.isLoading) {
    content = <p className={styles.emptyText}>Loading…</p>;
  } else {
    content = (
      <>
        <MemorySection
          title="About you"
          memories={userMemories}
          cap={USER_MEMORY_CAP}
          emptyText="Nothing yet. The assistant saves facts you share, or you can add one."
          onDeleteAll={deleteAll}
        />
        {deleteAllMutation.isError && (
          <p className={styles.errorText} role="alert">
            Couldn&apos;t delete your memories
          </p>
        )}
        {threadId !== undefined && (
          // Keyed by thread so an open draft does not carry over to another chat.
          <MemorySection
            key={threadId}
            title="This chat"
            memories={threadMemories}
            cap={THREAD_MEMORY_CAP}
            threadId={threadId}
            emptyText="No facts pinned to this chat."
          />
        )}
      </>
    );
  }

  return (
    <div className={styles.memoryView}>
      <div className={styles.toggleRow}>
        <span className={styles.toggleLabel}>
          <span id="memory-toggle-label">Use memory</span>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => setIsInfoOpen(true)}
            aria-label="About memory"
          >
            <FiInfo aria-hidden="true" />
          </button>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-labelledby="memory-toggle-label"
          className={enabled ? styles.switchOn : styles.switch}
          onClick={() => setEnabled(!enabled)}
        >
          <span className={styles.switchThumb} />
        </button>
      </div>
      {!enabled && (
        <p className={styles.offMessage}>
          Memory is off. The assistant won&apos;t read or save memories.
        </p>
      )}
      <div className={styles.scroller}>{content}</div>
      {isInfoOpen && <MemoryInfoModal onClose={() => setIsInfoOpen(false)} />}
    </div>
  );
}
